import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { SearchAutoComplete } from 'components/index.js'
import AnimatedNavbar from './components/AnimatedNavbar/AnimatedNavbar'
import SettingDropdown from './components/SettingDropdown'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FaUser } from 'react-icons/fa'
import { Film, LayoutDashboard, MessageSquare, Upload, Sparkles } from 'lucide-react'
import { creditModal, toggleAuthModal, updateSearchAction } from 'store/actions/header.action'
import { headerFilters, navbarMenuItems } from 'utils/constants'
import ThemeDropDown from '../../components/ThemeDropDown'
import useIsMobile from 'hooks/useIsMobile'
import { getModelsAction } from 'store/actions/models.action'
import { Button } from '@components/ui/button'
import MenuItem from './components/MenuItem'
import CreateButton from './components/CreateButton'

const Header = ({ displayLeftMenu }) => {
  const { t } = useTranslation('common')

  const router = useRouter()
  const { search } = router.query
  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const { data: models, loading } = useSelector((state) => state.models)
  const { subscription, user } = useSelector((state) => state.auth)
  const { activeChatTheme } = useSelector((state) => state.theme)

  const [inputSearch, setInputSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(null)
  const [activeSubmenu, setActiveSubmenu] = useState(null)

  // Define header buttons configuration
  const headerButtons = [
    {
      icon: LayoutDashboard,
      href: '/dashboard',
      variant: 'ghost',
      className: 'px-3',
    },
    {
      icon: MessageSquare,
      href: '/chat',
      variant: 'ghost',
      className: 'px-3',
    },
    {
      icon: Upload,
      action: 'upload',
      variant: 'ghost',
      className: 'px-3',
    },
  ]

  // Create options configuration
  const createOptions = [
    {
      label: 'Create',
      icon: Film,
      action: 'editor',
      variant: 'ghost',
    },
    {
      label: 'Create 2',
      icon: Sparkles,
      href: '/create',
      variant: 'gradient',
    },
  ]

  useEffect(() => {
    if (!models && !loading) dispatch(getModelsAction())
  }, [])

  useEffect(() => {
    dispatch(updateSearchAction(null, true))
  }, [router.pathname])

  useEffect(() => {
    setInputSearch(search)
  }, [search])

  const handleSearch = (value, select = true) => {
    setInputSearch(value)
    if (value?.trim() && select) {
      dispatch(updateSearchAction(value))
      router.push(`/model/${value}`)
    } else if (value?.trim() && !select) {
      dispatch(updateSearchAction(value))
      router.push(`/search?search=${value}`)
    }
  }

  const handleSubmit = (e, value) => {
    e.preventDefault()
    handleSearch(value, false)
  }

  const formatSearchResult = (item) => {
    return (
      <div className="flex flex-col cursor-pointer">
        <span className="text-sm font-bold">{item.name}</span>
        <span className="text-sm opacity-50 whitespace-wrap">{item.description}</span>
      </div>
    )
  }

  const openModal = (modalType) => {
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, modalType },
      },
      undefined,
      { shallow: true }
    )
  }

  const handleFilterClick = (e, i) => {
    e.preventDefault()
    const item = headerFilters[i]
    const searchValue = item === 'All AIs' ? 'All' : item === 'Top AIs' ? 'Top' : item?.replaceAll(' ', '_')
    setActiveIndex(i)
    router.push(`/explore-models?search=${searchValue}`)
  }

  // Handle button click, either open modal or navigate
  const handleButtonClick = (button) => {
    if (button.action) {
      openModal(button.action)
    }
  }

  return (
    <>
      <div
        className={`header fixed left-0 top-0 w-full z-[998] transition-all duration-300 shadow-md backdrop-blur-lg ${activeChatTheme != 'theme5' ? '!bg-black/95' : 'bg-white/95'}`}
      >
        <div className="flex items-center justify-between py-3 px-4 lg:px-6 xl:px-8 h-[64px] capitalize">
          {/* Logo and Navigation */}
          <div className="flex items-center gap-3 md:gap-8 w-1/4 md:w-[25%] min-w-max  lg:w-[18%] 2xl:w-[22%]">
            <Link className="text-black text-center text-sm font-semibold tracking-[0.14px]" href="/">
              <div className="inline-flex justify-end items-center logo h-[32px] shrink-0">
                <img
                  src={'/assets/common/StyleyLogo.png'}
                  alt="logo"
                  width={90}
                  height={14}
                  className={`w-[90px] md:w-auto ${activeChatTheme != 'theme5' ? '!invert' : 'brightness-0'} h-[14px]`}
                />
              </div>
            </Link>
            <div className="hidden md:flex items-center space-x-1 lg:space-x-3 2xl:space-x-6">
              {navbarMenuItems.map((item, index) => (
                <MenuItem
                  key={`menu-item-${index}`}
                  item={item}
                  isActive={activeSubmenu === index}
                  setActiveSubmenu={(active) => {
                    if (active) {
                      setActiveSubmenu(index)
                    } else {
                      setActiveSubmenu(null)
                    }
                  }}
                />
              ))}
            </div>
          </div>

          {/* Centered Search Bar */}
          <div className="searchbar w-[32%] 2xl:w-[33%] h-[44px] flex-shrink-0 hidden md:block px-2.5">
            <form onSubmit={(e) => handleSubmit(e, inputSearch)}>
              <SearchAutoComplete
                items={models}
                placeholder={'Search models or media'}
                onSearch={setInputSearch}
                onSelect={(item) => item && handleSearch(item.alias)}
                autoFocus
                formatResult={formatSearchResult}
                enableSearchIcon={true}
                enableEditIcon={true}
                maxResults={12}
                inputClassNames="!pl-10 rounded-full border-gray-300 focus:border-theme-primary transition-all duration-200"
                searchIconStyle="!right-auto !left-3"
                editIconStyle="!right-3"
                onEditIconClick={() => openModal('editor')}
              />
            </form>
          </div>

          {/* Right Side - Auth or User Controls */}
          {!user ? (
            <div className="flex hidden-sm gap-2 w-[30%] justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  dispatch(toggleAuthModal('sign_up'))
                }}
              >
                Sign Up
              </Button>

              <Button
                onClick={() => {
                  dispatch(toggleAuthModal('sign_in'))
                }}
              >
                Login
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex text-sm 2xl:text-sm items-center justify-end gap-2  2xl:gap-3 capitalize w-[35%]">
              <div className="hidden lg:flex gap-2">
                {/* Regular Buttons */}
                {headerButtons.map((button, index) =>
                  button.href ? (
                    <Link key={`btn-${index}`} href={button.href}>
                      <Button className={button.className} variant={button.variant}>
                        {button.icon && React.createElement(button.icon, { className: 'w-4 h-4' })}
                        {button.label}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className={button.className}
                      key={`btn-${index}`}
                      variant={button.variant}
                      onClick={() => handleButtonClick(button)}
                    >
                      {button.icon && React.createElement(button.icon, { className: 'w-4 h-4' })}
                      {button.label}
                    </Button>
                  )
                )}

                {/* Create Dropdown Button */}
                <CreateButton createOptions={createOptions} handleButtonClick={handleButtonClick} />
              </div>

              {/* Credits Display */}
              <div
                className={
                  'flex flex-row gap-[2px] sm:gap-2 items-center justify-center w-fit bg-[#F5F5F5] py-[8px] px-[12px] md:px-[15px] cursor-pointer'
                }
                onClick={() => {
                  dispatch(creditModal(true))
                }}
              >
                <Image src="/assets/common/credits.svg" alt="credits" width={16} height={16} />
                {' ' + (subscription?.credits ?? 0)}
              </div>

              {/* User Profile */}
              <div className="flex items-center gap-2">
                <div className="flex items-center z-[9999999]  gap-2 p-[3px]">
                  <AnimatedNavbar
                    duration={500}
                    navbarConfig={[
                      {
                        item: (
                          <div className="flex justify-center items-center w-10 h-10  cursor-pointer hover:opacity-90 transition-opacity">
                            <Link href={!isMobile ? '/account' : '#'}>
                              {user?.avatar_url ? (
                                <div className="rounded-full overflow-hidden border max-w-10 max-h-10 min-w-10 min-h-10 w-10 h-10">
                                  <Image
                                    className="object-cover object-top min-w-10 min-h-10 w-10 h-10 rounded-full"
                                    src={user?.avatar_url || '/placeholder.svg'}
                                    width={40}
                                    height={40}
                                    alt="user icon"
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center justify-center w-10 h-10 bg-theme-primary text-white rounded-full">
                                  <FaUser size={'18px'} />
                                </div>
                              )}
                            </Link>
                          </div>
                        ),
                        dropdown: <SettingDropdown onClick={() => {}} desktop />,
                      },
                    ]}
                  />
                </div>
              </div>
              <ThemeDropDown />
            </div>
          )}
        </div>

        {/* Filter Navigation */}
        <div
          className="w-full hidden md:flex justify-center items-center border-b border-white/10"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75))',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          <div className="flex justify-center items-center gap-6 md:gap-8 lg:gap-10 py-2 overflow-x-auto">
            {headerFilters.map((item, i) => (
              <Link
                key={`hf-${i}`}
                href={`/explore-models?search=${item === 'All AIs' ? 'All' : item === 'Top AIs' ? 'Top' : item?.replaceAll(' ', '_')}`}
                onClick={(e) => handleFilterClick(e, i)}
                className="relative block px-1 group"
              >
                <span
                  className={`text-[0.7rem] font-semibold tracking-wide transition-colors duration-200 ${
                    activeIndex === i ? 'text-white' : 'text-white/70'
                  } hover:text-white`}
                >
                  {item.toUpperCase()}
                </span>
                {activeIndex === i && (
                  <span className="absolute bottom-[-8px] left-0 w-full h-[2px] bg-white rounded-full"></span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Spacer to prevent content from being hidden under fixed header */}
      <div className="h-[104px] md:h-[104px]"></div>
    </>
  )
}

export default Header
